import React, { useCallback, useEffect, useState } from "react";
import classes from "../Management.module.scss";
import styles from "./DealerPipeline.module.scss";
import {
  getDealerAdminDeposit,
  recordDealerAdminDeposit,
  formatUsd,
  DEPOSIT_TYPE_LABELS,
} from "../../../services/dealerGarage";
import { formatDate, errorMessage, parseMoney, todayYmd } from "./dealerAdmin";

const METHODS = [
  ["quickbooks", "QuickBooks"],
  ["wire", "Wire transfer"],
  ["ach", "ACH"],
  ["check", "Check"],
  ["cash", "Cash"],
  ["card", "Card"],
  ["other", "Other"],
];

const emptyForm = () => ({
  type: "deposit",
  direction: "add",
  amount: "",
  date: todayYmd(),
  method: "quickbooks",
  reference: "",
  notes: "",
});

/**
 * One customer's deposit balance and its history (the deposit ledger), with a
 * form to record a deposit that arrived - usually a paid QuickBooks deposit
 * invoice - a refund, or a correction.
 *
 * The balance is the same number the auction uses for bidding power, so a
 * change here changes what the customer can bid.
 */
const DepositPanel = ({ userId, refreshKey = 0 }) => {
  const [ledger, setLedger] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    if (!userId) return;
    getDealerAdminDeposit(userId)
      .then((res) => setLedger(res?.message === "Success" ? res.data : null))
      .catch(() => setLedger(null));
  }, [userId]);

  // refreshKey: bumped by the invoice list beside it, whose payments can move
  // this balance.
  useEffect(() => { load(); }, [load, refreshKey]);

  const submit = async (e) => {
    e.preventDefault();
    const amount = parseMoney(form.amount);
    if (!(amount > 0)) {
      alert("Enter an amount.");
      return;
    }
    setBusy(true);
    try {
      const res = await recordDealerAdminDeposit({ userId, ...form, amount });
      if (res?.message === "Deposit updated") {
        setLedger(res.data);
        setForm(emptyForm());
        setOpen(false);
      } else {
        alert(errorMessage(res, "Could not record that."));
      }
    } catch (err) {
      alert("Could not record that.");
    } finally {
      setBusy(false);
    }
  };

  if (!ledger) return <p className={styles.hint}>Loading deposit...</p>;

  return (
    <div>
      <div className={classes.statsGrid} style={{ marginBottom: 8 }}>
        <div className={classes.statCard}>
          <div className={classes.statValue}>{formatUsd(ledger.balance)}</div>
          <div className={classes.statLabel}>Deposit balance · bids up to {formatUsd(ledger.balance * 10, { cents: false })}</div>
        </div>
      </div>

      {ledger.transactions.length > 0 ? (
        <ul className={styles.history}>
          {ledger.transactions.map((t) => (
            <li key={t._id}>
              <span>{formatDate(t.date || t.createdAt)}</span>
              <span>
                <strong>{t.amount > 0 ? "+" : ""}{formatUsd(t.amount)}</strong> {DEPOSIT_TYPE_LABELS[t.type] || t.type}
                {t.reference ? ` · ${t.reference}` : ""}
                {t.notes ? ` · ${t.notes}` : ""}
                <span className={styles.hint}> · balance {formatUsd(t.balanceAfter)}{t.recordedBy && t.recordedBy.firstName ? ` · ${t.recordedBy.firstName}` : ""}</span>
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <p className={styles.hint}>No deposit activity yet.</p>
      )}

      {!open ? (
        <div className={classes.formButtons}>
          <button type="button" className={classes.tableButton} onClick={() => setOpen(true)}>Record deposit / refund / correction</button>
        </div>
      ) : (
        <form className={styles.inlineForm} onSubmit={submit}>
          <div className={styles.grid2}>
            <div className={classes.formGroup}>
              <label className={classes.formLabel}>What</label>
              <select className={classes.formInput} value={form.type} onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))}>
                <option value="deposit">Deposit received</option>
                <option value="refund">Refund to customer</option>
                <option value="adjustment">Correction</option>
              </select>
            </div>
            {form.type === "adjustment" && (
              <div className={classes.formGroup}>
                <label className={classes.formLabel}>Direction</label>
                <select className={classes.formInput} value={form.direction} onChange={(e) => setForm((f) => ({ ...f, direction: e.target.value }))}>
                  <option value="add">Add to balance</option>
                  <option value="remove">Take from balance</option>
                </select>
              </div>
            )}
            <div className={classes.formGroup}>
              <label className={classes.formLabel}>Amount (USD)</label>
              <input className={classes.formInput} value={form.amount} inputMode="decimal" onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} />
            </div>
            <div className={classes.formGroup}>
              <label className={classes.formLabel}>Date</label>
              <input className={classes.formInput} type="date" value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} />
            </div>
            <div className={classes.formGroup}>
              <label className={classes.formLabel}>Method</label>
              <select className={classes.formInput} value={form.method} onChange={(e) => setForm((f) => ({ ...f, method: e.target.value }))}>
                {METHODS.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
              </select>
            </div>
            <div className={classes.formGroup}>
              <label className={classes.formLabel}>Reference</label>
              <input className={classes.formInput} value={form.reference} placeholder="QuickBooks invoice no." onChange={(e) => setForm((f) => ({ ...f, reference: e.target.value }))} />
            </div>
          </div>
          <div className={classes.formGroup}>
            <label className={classes.formLabel}>Note (staff only)</label>
            <input className={classes.formInput} value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} />
          </div>
          <div className={classes.formButtons}>
            <button type="submit" className={`${classes.tableButton} ${classes.tableSuccessButton}`} disabled={busy}>{busy ? "Saving..." : "Save"}</button>
            <button type="button" className={classes.tableButton} onClick={() => { setOpen(false); setForm(emptyForm()); }}>Cancel</button>
          </div>
        </form>
      )}
    </div>
  );
};

export default DepositPanel;
